import React, { useMemo, useState } from "react";
import {
  Typography,
  Stack,
  CircularProgress,
  Paper,
  Button,
  Box,
  Divider,
  Collapse,
} from "@mui/material";

import { Message, useConfirmMeetingTime, useRequestNewProposedTimes, } from "../../../store";
import { ConflictTimeslot } from "../../../types";
import Copyright from "../../copyright";
import ribbonBackground from "../../../assets/ribbon.png";

interface MeetingConfirmationInfoProps {
  userId: string,
  meetingId: string,
  joinCode: string,
  creatorId: string,
  proposedTimes: ConflictTimeslot[]
  setMessage: (message: Message) => void
}

const toSlotKey = (slot: ConflictTimeslot) => {
  return `${slot.startTime.toISOString()}-${slot.endTime.toISOString()}`;
};

const formatDayHeading = (value: Date) => {
  return value.toLocaleDateString("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
};

const formatTimeRange = (slot: ConflictTimeslot) => {
  const formatter = new Intl.DateTimeFormat("en-AU", {
    hour: "numeric",
    minute: "2-digit",
  });

  return `${formatter.format(slot.startTime)} - ${formatter.format(slot.endTime)}`;
};

const getSlotInformation = (timeslot: ConflictTimeslot): {
  message: string,
  color: string,
  valid: boolean,
} => {
  const now = new Date();

  if (timeslot.startTime < now) {
    return {
      message: "Past",
      color: "#6b7280",
      valid: false,
    };
  }

  if (timeslot.conflictingUsers.length > 0 || timeslot.patientConflict) {
    return {
      message: "Unavailable",
      color: "#6b7280",
      valid: false,
    };
  }

  return {
    message: "Available",
    color: "#15803d",
    valid: true,
  };
};

const ConfirmTime: React.FC<MeetingConfirmationInfoProps> = ({
  userId,
  meetingId,
  joinCode,
  creatorId,
  setMessage,
  proposedTimes,
}) => {

  const groupedTimesByDay = useMemo(() => {
    const grouped = new Map<string, ConflictTimeslot[]>();

    for (const slot of proposedTimes) {
      const dayKey = slot.startTime.toLocaleDateString("en-CA");

      if (!grouped.has(dayKey)) {
        grouped.set(dayKey, []);
      }

      grouped.get(dayKey)?.push(slot);
    }

    return Array.from(grouped.entries()).map(([dayKey, slots]) => ({
      dayKey,
      date: new Date(slots[0].startTime),
      slots,
    }));
  }, [proposedTimes]);

  const [inProgress, setInProgress] = useState(false);
  const [selectedSlotKey, setSelectedSlotKey] = useState<string | null>(null);

  const selectedSlot = useMemo(() => {
    if (!selectedSlotKey) {
      return null;
    }

    return proposedTimes.find((slot) => toSlotKey(slot) === selectedSlotKey) || null;
  }, [proposedTimes, selectedSlotKey]);

  const finaliseMeetingTimeSuccess = (message: string) => {
    console.log(message)

    setMessage({
      message: "Confirmed meeting time",
      type: "success"
    })

    setInProgress(false)
    setSelectedSlotKey(null)
  }

  const finaliseMeetingTimeFail = (error: any) => {
    console.error(error)
    const message = error?.message || "Failed to propose times"

    setMessage({
      message,
      type: "error"
    })

    setInProgress(false)
  }

  const requestNewProposedTimesSuccess = (message: string) => {
    console.log(message)

    setMessage({
      message: "Requested new meeting times",
      type: "success"
    })

    setInProgress(false)
    setSelectedSlotKey(null)
  }

  const requestNewProposedTimesFail = (error: any) => {
    console.error(error)
    const message = error?.message || "Failed to request new times"

    setMessage({
      message,
      type: "error"
    })

    setInProgress(false)
  }

  const { mutate: finaliseMeetingTime } = useConfirmMeetingTime(finaliseMeetingTimeSuccess, finaliseMeetingTimeFail)
  const { mutate: requestNewProposedTimes } = useRequestNewProposedTimes(requestNewProposedTimesSuccess, requestNewProposedTimesFail)

  const finaliseTime = (slot: ConflictTimeslot) => {
    setInProgress(true)

    finaliseMeetingTime({
      meetingId,
      userId,
      joinCode,
      startTime: slot.startTime,
      endTime: slot.endTime,
    })
  }

  const requestNewTimes = () => {
    setInProgress(true)

    requestNewProposedTimes({
      meetingId,
      userId,
      joinCode,
      targetId: creatorId,
    })
  }

  const confirmSelectedTime = () => {
    if (!selectedSlot) {
      return;
    }

    finaliseTime(selectedSlot);
  };

  return (
    <Stack
      justifyContent="center"
      alignItems="center"
      sx={{
        minHeight: "100vh",
        width: "100%",
        px: 2,
        py: 4,
        boxSizing: "border-box",
        backgroundImage:
          `linear-gradient(160deg, rgba(0, 99, 102, 0.73), rgba(0, 76, 131, 0.93)), url(${ribbonBackground})`,
        backgroundBlendMode: "normal",
        backgroundPosition: "center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Paper
        elevation={20}
        sx={{
          width: { xs: "100%", md: 900 },
          maxWidth: 1100,
          p: { xs: 2.5, md: 4 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRadius: 3,
          background:
            "linear-gradient(145deg, rgba(255, 255, 255, 0.64) 0%, rgba(255, 255, 255, 0.28) 100%)",
          border: "1px solid rgba(255, 255, 255, 0.5)",
          boxShadow:
            "0 24px 60px rgba(8, 39, 69, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.35)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
      >
        <Stack
          spacing={3}
          sx={{ width: "100%" }}
        >
          <Stack
            spacing={0}
          >
            <Typography variant="h4" textAlign="center">
              Confirm Meeting Time
            </Typography>

            <Typography variant="body1" textAlign="center">
              Select the meeting time you want to confirm or request a new time with the request time button.
            </Typography>
          </Stack>

          <Stack spacing={2.5}>
            {groupedTimesByDay.map((group) => (
              <Paper
                key={group.dayKey}
                elevation={0}
                sx={{
                  p: { xs: 1.5, md: 2 },
                  borderRadius: 2,
                  background:
                    "linear-gradient(145deg, rgba(255, 255, 255, 0.52) 0%, rgba(255, 255, 255, 0.22) 100%)",
                  border: "1px solid rgba(255, 255, 255, 0.48)",
                  boxShadow:
                    "0 10px 24px rgba(8, 39, 69, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.32)",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                }}
              >
                <Stack spacing={1}>
                  <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 700 }}>
                    {formatDayHeading(group.date)}
                  </Typography>

                  <Box
                    sx={{
                      width: "100%",
                      display: "grid",
                      gap: 1,
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        md: "repeat(3, minmax(0, 1fr))",
                      },
                    }}
                  >
                    {group.slots.map((slot) => {
                      const slotKey = toSlotKey(slot);
                      const isSelected = selectedSlotKey === slotKey;
                      const slotInformation = getSlotInformation(slot);
                      const isDisabledSlot = inProgress || !slotInformation.valid;

                      return (
                        <Paper
                          key={slotKey}
                          variant="outlined"
                          onClick={() => {
                            if (isDisabledSlot) {
                              return;
                            }

                            setSelectedSlotKey((previous) => previous === slotKey ? null : slotKey);
                          }}
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            border: isSelected ? "1px solid #19d26c" : "1px solid #cbe1cd",
                            backgroundColor: !slotInformation.valid
                              ? "#e5ebe7"
                              : isSelected
                                ? "#dbfee3"
                                : "#ffffff",
                            color: !slotInformation.valid
                              ? "#6b7280"
                              : "#1f2937",
                            cursor: isDisabledSlot ? "default" : "pointer",
                            transition: "all 0.15s ease-in-out",
                            "&:hover": isDisabledSlot
                              ? undefined
                              : {
                                backgroundColor: isSelected ? "#c1febf" : "#f8fcf8",
                              },
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {formatTimeRange(slot)}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.9rem",
                                color: slotInformation.color,
                              }}
                            >
                              {slotInformation.message}
                            </Typography>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>

          <Box>
            <Collapse
              in={!!selectedSlot}
              timeout={220}
              collapsedSize={0}
              sx={{
                transformOrigin: "top center",
              }}
            >
              <Button
                variant="contained"
                disabled={inProgress || !selectedSlot}
                onClick={confirmSelectedTime}
                fullWidth
                sx={{ borderRadius: "1.5rem" }}
              >
                Confirm
              </Button>
            </Collapse>

            <Divider
              sx={{
                mt: selectedSlot ? 3 : 0,
                borderColor: "rgba(255, 255, 255, 0.45)",
              }}
            />
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.5, md: 2 },
              borderRadius: 2,
              background:
                "linear-gradient(145deg, rgba(255, 255, 255, 0.52) 0%, rgba(255, 255, 255, 0.22) 100%)",
              border: "1px solid rgba(255, 255, 255, 0.48)",
              boxShadow:
                "0 10px 24px rgba(8, 39, 69, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.32)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent="space-between"
            >
              <Typography variant="body2" sx={{ color: "#1e293b" }}>
                None of the timelots above work for you? Request a new timeslot from the organiser
              </Typography>

              <Button
                variant="contained"
                disabled={inProgress}
                onClick={requestNewTimes}
                sx={{
                  minWidth: 230,
                  borderRadius: "1.5rem",
                  alignSelf: { xs: "stretch", sm: "center" },
                  whiteSpace: "nowrap",
                }}
              >
                Request New Timeslot
              </Button>
            </Stack>
          </Paper>

          {inProgress &&
            <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
              <CircularProgress size={40} />
              <Typography variant="body1">Loading...</Typography>
            </Stack>}

        </Stack>
      </Paper>
      <Copyright color="rgba(255, 255, 255, 0.7)" sx={{ mt: 5 }} />
    </Stack>
  );
};

export default ConfirmTime;
